/**
 * Раздел «Строительство» страницы «Аналитика» (US-04.8): верхняя плашка
 * из 5 показателей (ИФО строительства, прирост строительства, ИФО ввода
 * жилья, кол-во и общая стоимость проектов с ПСД) + виджеты «Строительство»
 * и «Ввод жилья» (таблица «Показатель | Значение»: объём в деньгах,
 * ИФО %, прирост %) в ряд + блок ПСД из двух
 * отдельных карточек друг под другом: «Проектно-сметная документация» (pie-chart
 * по типам: гос. / негос. / квазигос.) и «Реестр ПСД» (статус · стоимость ·
 * сроки по каждому проекту).
 *
 * Виджет без данных не рендерится; пустой реестр прячет реестр ПСД
 * (FR-5.5). Drill-through по проекту (FR-5.4) — вне текущей вёрстки:
 * детализация появится вместе с бэком.
 *
 * Источник данных — гибрид: демо-КАТО (HARD_DATA_KATOS)
 * → мок hardData/analyticsConstruction.json (уровень области: значения —
 * млрд тенге, плашка вычисляется из блоков и реестра, FR-2); остальные
 * КАТО (по выбранному в сайдбаре) — один запрос
 * /api/public/analytics/construction (DP-158, US-04.8): plashka → плашка,
 * construction / housing → виджеты объёмов, psd_by_category → pie по
 * типам инвесторов, psd_registry → реестр (статусы — готовые строки из
 * БД). Денежные значения API — млн тенге; стоимость проектов реестра —
 * млн тенге в обоих режимах.
 */
import { Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';

import { StatCard } from '../../../components/widgets/StatCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../components/ui/chart';
import { useKato } from '../../../contexts/KatoContext';
import { useYear } from '../../../contexts/YearContext';
import analyticsConstruction from '../../../hardData/analyticsConstruction.json';
import {
  PSD_PAGE_SIZE,
  getAnalyticsConstruction,
} from '../../../requests/analytics.request';
import { useAnalyticsSection } from '../useAnalyticsSection';
import type {
  ConstructionAnalyticsResponse,
  PsdRegistryRow,
} from '../../../types/analytics';
import { cn } from '../../../lib/cn';

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

const pct = (v: number | null): string | null =>
  v !== null ? `${fmt1(v)}%` : null;

const signedPct = (v: number | null): string | null =>
  v !== null ? `${v > 0 ? '+' : ''}${fmt1(v)}%` : null;

/** Типы проектов ПСД (FR-5.2) → цвета сегментов pie (токены темы). */
const PSD_TYPES = ['state', 'private', 'quasiState'] as const;
const PSD_COLORS: Record<string, string> = {
  state: '#4A63E7',
  private: '#A3CE63',
  quasiState: '#F2D9A4',
};

/** financing_source ответа → ключ подписи psd.financing.* (enum ГГЭ). */
const FINANCING_KEYS: Record<string, string> = {
  GOVERNMENTAL_INVESTMENT: 'governmental',
  NON_GOVERNMENTAL_INVESTMENT: 'nonGovernmental',
  QUASI_GOVERNMENTAL_INVESTMENT: 'quasiGovernmental',
};

/** Тип мока → тот же ключ подписи источника финансирования. */
const MOCK_FINANCING_KEYS: Record<string, string> = {
  state: 'governmental',
  private: 'nonGovernmental',
  quasiState: 'quasiGovernmental',
};

/** Карточка проекта (drill-through FR-5.4): null-поля скрываются. */
interface PsdDrillVM {
  name: string;
  financing: string | null;
  expertiseNumber: string | null;
  expertiseDate: string | null;
  expertisePlace: string | null;
  expertBin: string | null;
  customer: string | null;
  customerBin: string | null;
  designer: string | null;
  designerBin: string | null;
  objectKato: string | null;
  objectLocation: string | null;
}

interface PsdProjectVM {
  key: string;
  name: string;
  financing: string | null;
  customer: string | null;
  /** null — данных карточки нет: строка неактивна (FR-5.4). */
  drill: PsdDrillVM | null;
}

interface ConstructionSectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевой виджет —
   * «Строительство» (значение · ИФО · прирост, FR-2.2). */
  summary?: boolean;
}

export const ConstructionSection = ({
  summary = false,
}: ConstructionSectionProps = {}) => {
  const { t, i18n } = useTranslation();
  const isKz = i18n.resolvedLanguage === 'kz';

  const { data, loading, isHardKato } =
    useAnalyticsSection<ConstructionAnalyticsResponse>(
      'construction',
      getAnalyticsConstruction,
    );

  // --- Пагинация «Реестра ПСД» (DP-158): первая страница приходит
  // в ответе секции (psd_limit=PSD_PAGE_SIZE), листание — повторный
  // запрос секции с новым psd_offset, из ответа берётся только реестр ---
  const { selectedKato } = useKato();
  const { selectedYear } = useYear();
  const [psdPage, setPsdPage] = useState(0);
  const [psdPageRows, setPsdPageRows] = useState<PsdRegistryRow[] | null>(
    null,
  );
  const [psdPageLoading, setPsdPageLoading] = useState(false);

  // Смена территории/года → новый ответ секции → реестр с первой страницы.
  useEffect(() => {
    setPsdPage(0);
    setPsdPageRows(null);
  }, [data]);

  const openPsdPage = (page: number) => {
    if (page < 0 || selectedKato === null) return;
    setPsdPage(page);
    if (page === 0) {
      // Первая страница уже есть в ответе секции.
      setPsdPageRows(null);
      return;
    }
    setPsdPageLoading(true);
    getAnalyticsConstruction(selectedKato, selectedYear, page * PSD_PAGE_SIZE)
      .then((response) => setPsdPageRows(response.psd_registry))
      .catch(() => setPsdPageRows([]))
      .finally(() => setPsdPageLoading(false));
  };

  // Мок уровня области — млрд тенге; данные API — млн тенге.
  const moneyUnit = isHardKato
    ? t('analytics.construction.unitBillionTenge')
    : t('analytics.construction.unitMillionTenge');

  // --- Блоки «Строительство» / «Ввод жилья» в общей форме ---
  const construction: {
    value: number | null;
    ifo: number | null;
    growth: number | null;
  } = isHardKato
    ? analyticsConstruction.construction
    : {
        value: data?.construction?.value ?? null,
        ifo: data?.construction?.ifo ?? null,
        growth: data?.construction?.change_pct ?? null,
      };
  const housing: {
    value: number | null;
    ifo: number | null;
    growth: number | null;
  } = isHardKato
    ? analyticsConstruction.housing
    : {
        value: data?.housing?.value ?? null,
        ifo: data?.housing?.ifo ?? null,
        growth: data?.housing?.change_pct ?? null,
      };

  // --- Реестр ПСД (FR-5.3): наименование · источник финансирования ·
  // заказчик; карточка проекта (FR-5.4) собирается из полей ГГЭ —
  // без них строка неактивна ---
  const financingLabel = (source: string | null): string | null => {
    if (source === null) return null;
    const key = FINANCING_KEYS[source];
    return key !== undefined
      ? t(`analytics.construction.psd.financing.${key}`)
      : source;
  };

  const [drillProject, setDrillProject] = useState<PsdDrillVM | null>(null);

  const projects: PsdProjectVM[] = isHardKato
    ? analyticsConstruction.psd.projects.map((project) => ({
        key: project.key,
        name: isKz ? project.name.kz : project.name.ru,
        financing: t(
          `analytics.construction.psd.financing.${MOCK_FINANCING_KEYS[project.type] ?? 'governmental'}`,
        ),
        customer: isKz ? project.customer.kz : project.customer.ru,
        // Полей заключения ГГЭ в демо-моке нет — строки неактивны.
        drill: null,
      }))
    : (psdPageRows ?? data?.psd_registry ?? []).map((row, index) => {
        const name = row.project ?? '—';
        const financing = financingLabel(row.financing_source);
        // Карточка есть, когда бэк отдал хоть одно её поле (ГГЭ).
        const drillFields = [
          row.conclusion_number,
          row.conclusion_date,
          row.examination_company,
          row.examination_company_bin,
          row.customer,
          row.customer_bin,
          row.designer,
          row.designer_bin,
          row.kato,
          row.object_location,
        ];
        const hasDrill = drillFields.some(
          (field) => field !== null && field !== undefined,
        );
        return {
          key: String(psdPage * PSD_PAGE_SIZE + index),
          name,
          financing,
          customer: row.customer ?? null,
          drill: hasDrill
            ? {
                name,
                financing,
                expertiseNumber: row.conclusion_number ?? null,
                expertiseDate: row.conclusion_date ?? null,
                expertisePlace: row.examination_company ?? null,
                expertBin: row.examination_company_bin ?? null,
                customer: row.customer ?? null,
                customerBin: row.customer_bin ?? null,
                designer: row.designer ?? null,
                designerBin: row.designer_bin ?? null,
                objectKato: row.kato ?? null,
                objectLocation: row.object_location ?? null,
              }
            : null,
        };
      });

  // Итог реестра для пагинации; null — бэк без пагинации (или демо-мок).
  const psdTotal = isHardKato ? null : (data?.psd_registry_total ?? null);

  // --- Pie «ПСД по типам» (FR-5.2): демо-КАТО — распределение реестра
  // по type; остальные — суммы инвесторов psd_by_category ---
  const psdTypeCounts: Record<(typeof PSD_TYPES)[number], number> = isHardKato
    ? {
        state: analyticsConstruction.psd.projects.filter(
          (project) => project.type === 'state',
        ).length,
        private: analyticsConstruction.psd.projects.filter(
          (project) => project.type === 'private',
        ).length,
        quasiState: analyticsConstruction.psd.projects.filter(
          (project) => project.type === 'quasiState',
        ).length,
      }
    : (data?.psd_by_category ?? []).reduce(
        (acc, category) => ({
          state: acc.state + category.government_investors_count,
          private: acc.private + category.non_government_investors_count,
          quasiState:
            acc.quasiState + category.quasi_government_investors_count,
        }),
        { state: 0, private: 0, quasiState: 0 },
      );

  const psdSegments = PSD_TYPES.flatMap((type) => {
    const count = psdTypeCounts[type];
    if (count === 0) return [];
    return [
      {
        key: type,
        name: t(`analytics.construction.psd.types.${type}`),
        value: count,
        color: PSD_COLORS[type],
      },
    ];
  });
  const psdSegmentsSum = psdSegments.reduce(
    (acc, segment) => acc + segment.value,
    0,
  );

  const psdConfig: ChartConfig = Object.fromEntries(
    psdSegments.map((segment) => [
      segment.key,
      { label: segment.name, color: segment.color },
    ]),
  );

  // --- Плашка (FR-2): демо-КАТО — вычисляется из блоков и реестра,
  // остальные — plashka ответа ---
  const psdCount = isHardKato
    ? projects.length > 0
      ? projects.length
      : null
    : (data?.plashka.psd_count ??
      (projects.length > 0 ? projects.length : null));
  const psdCostMln = isHardKato
    ? analyticsConstruction.psd.projects.reduce(
        (acc: number | null, project) =>
          project.cost === null ? acc : (acc ?? 0) + project.cost,
        null,
      )
    : (data?.plashka.psd_total_cost ?? null);

  const constructionIfo = isHardKato
    ? construction.ifo
    : (data?.plashka.construction_ifo ?? construction.ifo);
  const constructionGrowth = isHardKato
    ? construction.growth
    : (data?.plashka.construction_growth ?? construction.growth);
  const housingIfo = isHardKato
    ? housing.ifo
    : (data?.plashka.housing_ifo ?? housing.ifo);

  // Плашка всегда показывает все показатели; нет данных → «—»
  // (замечание дизайн-ревью).
  const cards: Array<{ key: string; value: string; unit: string | null }> = [
    {
      key: 'constructionIfo',
      value: pct(constructionIfo) ?? '—',
      unit: null,
    },
    {
      key: 'constructionGrowth',
      value: signedPct(constructionGrowth) ?? '—',
      unit: null,
    },
    { key: 'housingIfo', value: pct(housingIfo) ?? '—', unit: null },
    {
      key: 'psdCount',
      value: psdCount !== null ? fmt(psdCount) : '—',
      unit: t('units.pieces'),
    },
    {
      key: 'psdCost',
      value:
        psdCostMln !== null
          ? isHardKato
            ? fmt1(psdCostMln / 1000)
            : fmt1(psdCostMln)
          : '—',
      unit: isHardKato
        ? t('analytics.construction.unitBillionTenge')
        : t('analytics.construction.unitMillionTenge'),
    },
  ];

  // --- Виджеты «Строительство» / «Ввод жилья» (FR-3, FR-4): всегда
  // видны (без данных — «Нет данных», замечание ревью); на витрине
  // «Все» — только «Строительство» (US-04.1 FR-2.2) ---
  const volumeWidgets = (
    [
      { key: 'construction', titleKey: 'constructionTitle', ...construction },
      { key: 'housing', titleKey: 'housingTitle', ...housing },
    ] as Array<{
      key: string;
      titleKey: string;
      value: number | null;
      ifo: number | null;
      growth: number | null;
    }>
  ).filter((widget) => !summary || widget.key === 'construction');

  return (
    <section className="space-y-4" data-testid="analytics-construction">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.construction.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Building2
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Пока грузятся данные с бэка — лоадер вместо виджетов. */}
      {loading ? (
        <BlockLoader />
      ) : (
        <>

      {!summary && (
        <div className="flex flex-wrap gap-3.5">
          {cards.map((card) => (
            <div key={card.key} className="min-w-52 flex-1">
              <StatCard
                testId={`analytics-card-${card.key}`}
                title={t(`analytics.construction.cards.${card.key}`)}
                value={card.value}
                unit={card.unit}
              />
            </div>
          ))}
        </div>
      )}

      {/* «Строительство» / «Ввод жилья» — таблица «Показатель | Значение»
          (замечание дизайн-ревью, референс v1). */}
      {volumeWidgets.length > 0 && (
        <div className="grid gap-3.5 xl:grid-cols-2">
          {volumeWidgets.map((widget) => {
            const rows = [
              {
                key: 'value',
                label: t(
                  `analytics.construction.volume.${widget.key}ValueRow`,
                  { unit: moneyUnit },
                ),
                value: widget.value !== null ? fmt1(widget.value) : '—',
              },
              {
                key: 'ifo',
                label: t('analytics.construction.volume.ifoRow'),
                value: widget.ifo !== null ? fmt1(widget.ifo) : '—',
              },
              {
                key: 'growth',
                label: t('analytics.construction.volume.growthRow'),
                value:
                  widget.growth !== null
                    ? `${widget.growth > 0 ? '+' : ''}${fmt1(widget.growth)}`
                    : '—',
              },
            ];
            const hasData =
              widget.value !== null ||
              widget.ifo !== null ||
              widget.growth !== null;
            return (
              <Card key={widget.key} className="shadow-card">
                <CardHeader>
                  <CardTitle>
                    {t(`analytics.construction.volume.${widget.titleKey}`)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!hasData ? (
                    <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
                  ) : (
                  <div data-testid={`analytics-construction-${widget.key}`}>
                    <div className="flex items-center gap-3 px-3 pb-2 text-sm text-text-faint">
                      <span className="min-w-0 flex-1">
                        {t('analytics.construction.volume.columnIndicator')}
                      </span>
                      <span className="w-24 shrink-0 text-right">
                        {t('analytics.construction.volume.columnValue')}
                      </span>
                    </div>
                    <ul>
                      {rows.map((row) => (
                        <li
                          key={row.key}
                          data-testid={`analytics-construction-${widget.key}-${row.key}`}
                          className="flex items-center gap-3 border-b border-surface-border px-3 py-4 text-sm last:border-b-0 last:pb-0"
                        >
                          <span className="min-w-0 flex-1 font-semibold leading-snug">
                            {row.label}
                          </span>
                          <span className="w-24 shrink-0 text-right font-extrabold tabular-nums">
                            {row.value}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* FR-5.5: сводка (pie) и реестр скрываются независимо; нет данных
          ни там, ни там — блок ПСД не рендерится совсем (без пустого
          каркаса). Карточки — двумя отдельными строками на всю ширину. */}
      {!summary &&
        (psdSegments.length > 0 ||
          projects.length > 0 ||
          psdPageLoading) && (
        <div className="grid gap-3.5">
          {psdSegments.length > 0 && (
          <Card className="shadow-card">
            <CardHeader className="flex-row items-baseline justify-between gap-4 space-y-0">
              <CardTitle>{t('analytics.construction.psd.title')}</CardTitle>
              {psdCount !== null && (
                <span className="shrink-0 text-sm text-text-faint">
                  {t('analytics.construction.psd.totalLabel')}:{' '}
                  <span className="font-bold text-text">
                    {fmt(psdCount)}
                  </span>
                </span>
              )}
            </CardHeader>
            <CardContent data-testid="analytics-psd-types">
              {/* Pie и легенда — в два столбца (замечание ревью):
                  донат слева, типы с количеством — справа. */}
              <div className="flex flex-wrap items-center gap-x-10 gap-y-6">
                <div className="w-full max-w-[220px] shrink-0">
                <ChartContainer
                  config={psdConfig}
                  className="aspect-square"
                >
                  <PieChart>
                    <ChartTooltip
                      cursor={false}
                      content={<ChartTooltipContent hideLabel />}
                    />
                    <Pie
                      data={psdSegments}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="55%"
                      outerRadius="98%"
                      strokeWidth={2}
                    >
                      {psdSegments.map((segment) => (
                        <Cell key={segment.key} fill={segment.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ChartContainer>
                </div>
                {/* Легенда с пунктирной отбивкой и долей — значение
                    рядом с подписью, а не у дальнего края карточки. */}
                <ul className="min-w-64 max-w-xl flex-1 space-y-2.5">
                  {psdSegments.map((segment) => (
                    <li
                      key={segment.key}
                      data-testid={`analytics-psd-type-${segment.key}`}
                      className="flex items-baseline gap-2 text-sm"
                    >
                      <span
                        className="size-2.5 shrink-0 self-center rounded-sm"
                        style={{ backgroundColor: segment.color }}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 leading-snug text-text-muted">
                        {segment.name}
                      </span>
                      <span className="flex-1 border-b border-dotted border-surface-border" />
                      <span className="whitespace-nowrap font-extrabold tabular-nums">
                        {fmt(segment.value)}
                      </span>
                      <span className="w-12 shrink-0 text-right text-xs tabular-nums text-text-faint">
                        {psdSegmentsSum > 0
                          ? `${fmt1((segment.value / psdSegmentsSum) * 100)}%`
                          : '—'}
                      </span>
                    </li>
                  ))}
                </ul>
                </div>
            </CardContent>
          </Card>
          )}

          {/* Реестр ПСД (FR-5.3): наименование · источник финансирования ·
              заказчик; клик по строке — карточка проекта (FR-5.4). */}
          {(projects.length > 0 || psdPageLoading) && (
          <Card
            className="shadow-card"
            data-testid="analytics-psd-registry"
          >
            <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-2 space-y-0">
              <CardTitle>
                {t('analytics.construction.psd.registryTitle')}
              </CardTitle>
              <span className="shrink-0 text-xs text-text-faint">
                {t('analytics.construction.psd.rowHint')}
              </span>
            </CardHeader>
            <CardContent>
                <>
                <div className="flex items-center gap-4 px-3 pb-2 text-sm leading-tight text-text-faint">
                  <span className="min-w-0 flex-1">
                    {t('analytics.construction.psd.columnProject')}
                  </span>
                  <span className="hidden w-56 shrink-0 sm:block">
                    {t('analytics.construction.psd.columnFinancing')}
                  </span>
                  <span className="hidden w-64 shrink-0 lg:block">
                    {t('analytics.construction.psd.columnCustomer')}
                  </span>
                </div>
                {psdPageLoading ? (
                  <BlockLoader />
                ) : (
                <ul>
                  {projects.map((project) => (
                    <li
                      key={project.key}
                      className="border-b border-surface-border last:border-b-0"
                    >
                      {/* FR-5.4: клик — карточка проекта; нет данных
                          карточки — строка неактивна. */}
                      <button
                        type="button"
                        disabled={project.drill === null}
                        onClick={() => setDrillProject(project.drill)}
                        data-testid={`analytics-psd-project-${project.key}`}
                        className={cn(
                          'flex w-full items-center gap-4 px-3 py-3.5 text-left text-sm',
                          project.drill !== null
                            ? 'cursor-pointer hover:bg-brand-50/60'
                            : 'cursor-default',
                        )}
                      >
                        <span className="min-w-0 flex-1 font-semibold leading-snug">
                          {project.name}
                        </span>
                        <span className="hidden w-56 shrink-0 leading-snug text-text-muted sm:block">
                          {project.financing ?? '—'}
                        </span>
                        <span className="hidden w-64 shrink-0 leading-snug text-text-muted lg:block">
                          {project.customer ?? '—'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                )}
                {/* Пагинация (DP-158): «N–M из K» + листание страниц. */}
                {psdTotal !== null && psdTotal > PSD_PAGE_SIZE && (
                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-surface-border px-3 pt-3">
                    <span
                      className="text-sm tabular-nums text-text-faint"
                      data-testid="analytics-psd-page-info"
                    >
                      {t('analytics.construction.psd.pageInfo', {
                        from: fmt(psdPage * PSD_PAGE_SIZE + 1),
                        to: fmt(psdPage * PSD_PAGE_SIZE + projects.length),
                        total: fmt(psdTotal),
                      })}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <button
                        type="button"
                        aria-label={t('analytics.construction.psd.prevPage')}
                        disabled={psdPage === 0 || psdPageLoading}
                        onClick={() => openPsdPage(psdPage - 1)}
                        data-testid="analytics-psd-prev"
                        className="flex size-8 items-center justify-center rounded-lg border border-surface-border text-text transition-colors hover:bg-surface-muted disabled:cursor-not-allowed disabled:text-text-faint disabled:hover:bg-transparent"
                      >
                        <ChevronLeft className="size-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        aria-label={t('analytics.construction.psd.nextPage')}
                        disabled={
                          (psdPage + 1) * PSD_PAGE_SIZE >= psdTotal ||
                          psdPageLoading
                        }
                        onClick={() => openPsdPage(psdPage + 1)}
                        data-testid="analytics-psd-next"
                        className="flex size-8 items-center justify-center rounded-lg border border-surface-border text-text transition-colors hover:bg-surface-muted disabled:cursor-not-allowed disabled:text-text-faint disabled:hover:bg-transparent"
                      >
                        <ChevronRight className="size-4" aria-hidden="true" />
                      </button>
                    </span>
                  </div>
                )}
                </>
            </CardContent>
          </Card>
          )}
        </div>
      )}

      {/* Карточка проекта (drill-through, FR-5.4): секции «Заключение
          экспертизы» / «Проект» / «Участники» / «Объект»; строки без
          данных скрываются. */}
      <Dialog
        open={drillProject !== null}
        onOpenChange={(open) => {
          if (!open) setDrillProject(null);
        }}
      >
        <DialogContent
          className="max-h-[85vh] w-[min(92vw,56rem)] max-w-4xl overflow-y-auto"
          data-testid="analytics-psd-card"
        >
          {drillProject !== null && (
            <>
              <DialogHeader>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-600">
                  {t('analytics.construction.psd.card.title')}
                </p>
                <DialogTitle className="text-left text-base leading-snug">
                  {drillProject.name}
                </DialogTitle>
                {drillProject.financing !== null && (
                  <span className="w-fit rounded-full bg-brand-50 px-2.5 py-0.5 text-[13px] font-semibold text-brand-600">
                    {drillProject.financing}
                  </span>
                )}
              </DialogHeader>
              {(
                [
                  [
                    'expertise',
                    [
                      ['expertiseNumber', drillProject.expertiseNumber],
                      ['expertiseDate', drillProject.expertiseDate],
                      ['expertisePlace', drillProject.expertisePlace],
                      ['expertBin', drillProject.expertBin],
                    ],
                  ],
                  [
                    'project',
                    [
                      ['name', drillProject.name],
                      ['financing', drillProject.financing],
                    ],
                  ],
                  [
                    'participants',
                    [
                      ['customer', drillProject.customer],
                      ['customerBin', drillProject.customerBin],
                      ['designer', drillProject.designer],
                      ['designerBin', drillProject.designerBin],
                    ],
                  ],
                  [
                    'object',
                    [
                      ['objectKato', drillProject.objectKato],
                      ['objectLocation', drillProject.objectLocation],
                    ],
                  ],
                ] as Array<[string, Array<[string, string | null]>]>
              ).map(([section, fields]) => {
                const known = fields.filter(
                  (field): field is [string, string] => field[1] !== null,
                );
                if (known.length === 0) return null;
                return (
                  <div key={section} data-testid={`analytics-psd-card-${section}`}>
                    <h4 className="border-b border-surface-border pb-1.5 text-xs font-bold uppercase tracking-wide text-text-faint">
                      {t(`analytics.construction.psd.card.${section}`)}
                    </h4>
                    <dl>
                      {known.map(([field, value]) => (
                        <div
                          key={field}
                          className="flex items-baseline gap-4 border-b border-dashed border-surface-border py-2 text-sm last:border-b-0"
                        >
                          <dt className="w-44 shrink-0 leading-snug text-text-muted">
                            {t(`analytics.construction.psd.card.${field}`)}
                          </dt>
                          <dd className="min-w-0 flex-1 font-semibold leading-snug">
                            {value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                );
              })}
            </>
          )}
        </DialogContent>
      </Dialog>
        </>
      )}
    </section>
  );
};
